'use client';
import { ChangeEvent, ReactNode } from 'react';
import Image from 'next/image';
import cn from 'classnames';

import DownChevronRed from 'public/img/icon/chevron-down.svg';
import DownChevronBlack from 'public/img/icon/chevron-down-black.svg';
import './Select.style.scss';

type SelectProps = {
  /** className to use for overriding styling */
  className?: string;
  /** Optional static icon to display with every option on the select. Sticks to the left side */
  staticIcon?: unknown;
  /** A group of `<option>` elements */
  children: ReactNode;
  /** Value to display */
  value?: string;
  /** handler for onChange events */
  onChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  /** handler for onClick events */
  onClick?: () => void;
  /** Defaults to `16`. Put in any number to change the size of both the `staticIcon` and dropdown arrow icon */
  iconSize?: number;
  /** Whether dropdown is disabled */
  disabled?: boolean;

  /**
   * @default outline
   *
   * but a bolded option is also available and just as attractive
   */
  variant?: 'outline' | 'bolded';
};

/**
 * A pretty and stylized uncontrolled Select element.
 * Use just like you would a regular `<select>` HTML element with
 * `onChange` and `value` props and pass state down from higher components
 * @param props
 * @returns
 */
function Select(props: SelectProps) {
  const {
    className = '',
    staticIcon,
    children,
    value,
    onChange,
    onClick,
    iconSize = 16,
    variant = 'outline',
    disabled = false
  } = props;

  return (
    <div className={cn('Select', variant, className)}>
      {!!staticIcon && (
        <Image
          className='Select-staticIcon'
          src={staticIcon as string}
          alt='Dropdown Icon Graphic'
          height={iconSize}
          width={iconSize}
        />
      )}
      <select
        className={cn('stylized', { 'with-static-icon': staticIcon })}
        value={value}
        onChange={onChange}
        onClick={onClick}
        disabled={disabled}
      >
        {children}
      </select>
      {variant === 'outline' && (
        <Image
          className='Select-downArrow'
          src={DownChevronRed}
          alt='Dropdown down arrow graphic'
          height={iconSize}
          width={iconSize}
        />
      )}
      {variant === 'bolded' && (
        <Image
          className='Select-downArrow'
          src={DownChevronBlack}
          alt='Dropdown down arrow graphic'
          height={iconSize}
          width={iconSize}
        />
      )}
    </div>
  );
}

export default Select;
